import { useRef, useEffect } from 'react';
import { type Timestamp } from '../services/Timestamp';

type DebouncedFunction<T extends (...args: any[]) => void> = (...args: Parameters<T>) => void;

export function useDebouncing<T extends (...args: any[]) => void>(callback: T, wait: Timestamp): DebouncedFunction<T> {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  function debouncedFunction(...args: Parameters<T>): void {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, wait.toMilliseconds());
  }

  useEffect(() => {
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFunction;
}
